/* eslint-disable no-unused-vars */
import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useParams } from 'react-router-dom';
import useFetchList from 'hooks/useFetchList';
import { AppRouteContext } from 'contexts';
import showServerError from 'helpers/showError';
import { useDispatch, useSelector } from 'react-redux';
import { NULL_QUERY_DELAY } from '../../Shared/constants';
import { GET_CLAIMS_COLUMNS } from '../constants';
import { ALL_ROUTES } from '../../../config/routes';
import OutstandingClaimReportPdfModal
  from './widgets/OutstandingClaimReportPdfModal/OutstandingClaimReportPdfModal';
import { checkPermission } from '../../../helpers/checkPermissions';
import permissions from '../../../config/permissions';
import { ReactComponent as DownloadIcon } from '../../../assets/icons/file_download_black.svg';
import { useMutation } from 'react-fetching-library';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../redux_store/reducer/reducers/notificationsReducer';
import { downloadFile } from '../../../helpers/DownloadPDF';
import QhsTable from '../../Shared/Table/Table';
import MenuItem from '../../Shared/Table/widgets/MenuItem';
import { convertDataFromApi, useGenerateColumns } from '../../Shared/Table/helpers';
import TableActions from './widgets/TableActions';
import { TABLE_KEYS } from '../../../redux_store/reducer/reducers/tableReducer/tableReducer';
/*
  could be rendered in 2 cases:
  - all account claims
  - claims for a special user (patientUuid in URL params)
*/

const ClaimsReportingList = forwardRef(({
  setFilterStyle,
  setTotalCount,
  tableTitle,
  q,
  hideMenuActions,
  columnList,
}, ref) => {
  const { en: { titles, notifications, errors } } = useSelector((state) => state.i18n);
  const { setAppRoute } = useContext(AppRouteContext);
  const dispatch = useDispatch();
  const { accountUuid: paramAccountUuid, } = useParams();
  const {
    metadata: {
      currentAccount,
      currentAccountPermissions,
    },
    isDistributionSponsor,
  } = useSelector((state) => state.user);
  const [modifiedClaims, setModifiedClaims] = useState();
  const [openDownloadPdfModal, toggleDownloadPdfModal] = useState(false);

  const hasLoadFilesPermission = useMemo(
    () => checkPermission(
      currentAccountPermissions,
      permissions.SURVEYS_GET,
    ),
    [currentAccountPermissions],
  );
  const tableKey = isDistributionSponsor ? TABLE_KEYS.claimsDSTable : TABLE_KEYS.claimsTable;
  const columns = useGenerateColumns(columnList || GET_CLAIMS_COLUMNS, tableKey, {}, {}).columns;
  const fields = columns.map((columnData) => columnData.accessorKey).join();

  const baseEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid
    }/claims`;
  const {
    items,
    totalCount,
    aggregations,
    reloadData,
    clearFilter,
    sort,
    onSort,
    filter,
    onFilter,
    loading: dataLoading,
    onLoadMoreRows,
  } = useFetchList({
    baseEndpoint,
    baseParams: {
      fields: fields,
      q: q || undefined,
    },
    tableKey,
    canLoad: fields.length,
  });

  const [prevTotalCount, setPrevTotalCount] = useState(totalCount);

  useEffect(() => {
    if (prevTotalCount !== totalCount) {
      setFilterStyle(!!filter?.reduce((accumulator, { value }) => {
        return accumulator + value.toString().replaceAll(' ', '').length;
      }, 0));
      setPrevTotalCount(totalCount);
      setTotalCount && setTotalCount(totalCount);
    }
  }, [filter, totalCount]);

  useEffect(() => {
    if (items) {
      const newModifiedItems = convertDataFromApi(items, tableKey, filter, {
        resendInvitationAction: (claim) => !claim.invitationResendAllowed,
      });
      setModifiedClaims(newModifiedItems);
    }
  }, [items]);

  const handleRowClick = (claim) => {
    const accountUuid = claim.accountUUID;
    const claimUuid = claim.uuid;
    const patientUuid = claim.patientUUID;
    const isItChildAccount = accountUuid !== currentAccount.uuid && isDistributionSponsor;
    setAppRoute({
      path: (isItChildAccount ? ALL_ROUTES.claim.editDS : ALL_ROUTES.claim.edit).path,
      title: `${titles.editClaim}`,
      params: {
        currentAccountUuid: currentAccount.uuid,
        accountUuid: isItChildAccount ? accountUuid : null,
        claimUuid,
        patientUuid,
      },
    });
  };

  const reloadClaimsReportingList = (hasFilter) => {
    if (!hasFilter) {
      clearFilter();
    } else {
      reloadData({ delay: NULL_QUERY_DELAY });
    }
  };

  useImperativeHandle(ref, () => ({
    reloadList(v) {
      reloadClaimsReportingList(v);
    },
  }));

  // const canGetClaim = checkPermission(
  //   isDistributionSponsor ? childAccountPermissions : currentAccountPermissions,
  //   isDistributionSponsor
  //     ? permissions.CLAIM_GET
  //     : permissions.CLAIM_GET_LIMITED,
  // );

  const importFile = (claim) => ({
    method: 'GET',
    endpoint: `/accounts/${claim.accountUUID}/surveys/${claim.entityUUID}/program-report/pdf`,
    responseType: 'blob',
  });

  const { mutate: importMutate, loading: isPDFLoading } = useMutation(importFile);

  const downloadProgramPDF = async (e, claim, setAnchorEl) => {
    const { payload, error, status } = await importMutate(claim);
    if (!error) {
      downloadFile(
        new Blob([payload]),
        `Program: ${claim.entityName} - ${claim.patientLastName} ${claim.patientFirstName} ${claim.entityOrderTimestamp}.pdf`,
      );
      dispatch(
        enqueueSnackbar({
          message: notifications.pdfSucceededSaved,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
    } else {
      switch (status) {
        case 422: {
          const obj = JSON.parse(await payload.text());
          Object.keys(obj.validationErrors).forEach(key =>
            showServerError(dispatch, obj.validationErrors[key]));
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
    }
    setAnchorEl && setAnchorEl(null);
  };

  const checkIsDownloadProgramPDFDisabled = (claimUuid) => {
    if (dataLoading || !modifiedClaims || !hasLoadFilesPermission) {
      return true;
    }
    let isDisabled = true;

    modifiedClaims.forEach((claim) => {
      if (claim.uuid === claimUuid && claim.entityType === 'Program') {
        isDisabled = false;
      }
    });

    return isDisabled;
  };

  return (
    <>
      <OutstandingClaimReportPdfModal
        openDownloadPdfModal={openDownloadPdfModal}
        toggleDownloadPdfModal={toggleDownloadPdfModal}
      />
      <QhsTable
        columns={columns}
        data={modifiedClaims || []}
        fetchNextPage={onLoadMoreRows}
        isFetching={dataLoading}
        isLoading={dataLoading && !modifiedClaims}
        totalRowCount={totalCount}
        sorting={sort}
        setSorting={onSort}
        filter={filter}
        setFilters={onFilter}
        tableKey={tableKey}
        onRowClick={handleRowClick}
        reloadData={reloadClaimsReportingList}
        tableTitle={tableTitle}
        showRefreshTableButton
        menuActions={!hideMenuActions && <TableActions
          totalCount={totalCount}
          tableSort={sort}
          tableFilter={filter}
          columns={columns}
          filter={filter}
          aggregations={aggregations}
        />}
        renderRowActionMenuItems={({ row, setAnchorEl }) => (
          <>
            <MenuItem
              title={titles.downloadProgram}
              setAnchorEl={() => {
              }}
              icon={<DownloadIcon width={16} height={16} />}
              isLoading={isPDFLoading}
              onClick={(e) => downloadProgramPDF(e, row.original, setAnchorEl)}
              isDisabled={checkIsDownloadProgramPDFDisabled}
              rowUuid={row.original.uuid}
            />
          </>
        )}
      />
    </>
  );
});

export default ClaimsReportingList;
